import { useCallback, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { flatJoin, flatten } from 'shared/util'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { validationMessages } from 'constants/validation'
import {
	FormControl, FormControlLabel, FormHelperText,
	InputAdornment, OutlinedInput, Typography,
	Radio, RadioGroup, ToggleButton, ToggleButtonGroup,
	Stack, StackProps, Box	
} from '@mui/material'

import { Section } from 'components/controls/section'
import { OptionSelect } from 'components/controls/optionSelect'
import { Loading } from 'components/controls/loading'
import { ErrorBlock } from 'components/controls/errorInfo'

import { useListBigTag } from 'interface/carenet/bigTag'
import { useListProductCategory } from 'interface/carenet/product'
import { ProductResult } from 'interface/carenet/product/types'

import { WeekDay, WeekDays } from 'shared/weekDay'
import {
	PredictionCondition, PredictionConditionMrp, 
	MrpPredictionAreaMrpType, MrpPredictionAreaMrpTypes, 
	MrpPredictionContentsType, MrpPredictionContentsTypes,
	MrpPredictionRecommendPriolity, MrpPredictionRecommendPriolities, 
	MrpPredictionServiceType, MrpPredictionServiceTypes,
	SpecialProducts
} from 'interface/carenet/prediction/types'

const {
  Required
} = validationMessages

const formSchema = yup.object().shape({
	productId: yup.array()
		.of(yup.number().required(Required)),
	startWeekday: yup.string().required(Required),
	startHour: yup.number().required(Required),
	days: yup.number().required(Required),
	serviceType: yup.string().nullable(),
	contentsType: yup.string().nullable(),
	contentsDuration: yup.number().nullable(),
	completeUp: yup.number().required(Required),
	recommendPriolity: yup.string().nullable(),
	areaMrp: yup.string().nullable(),
	categoryCode: yup.array().of(yup.string().required(Required)),
	bigTagId: yup.array().of(yup.number().required(Required))
})
type FormType = yup.InferType<typeof formSchema>

type useMrpPredictionFormProps = {
	productList: ProductResult[]
	condition: PredictionCondition | null
}
export const useMrpPredictionForm = ({
	productList,
	condition
}: useMrpPredictionFormProps) => {
	const categoryListState = useListProductCategory()
	const bigTagListState = useListBigTag()

	const { masterLoading, masterErrors } = useMemo(
		() => ({
			masterLoading: categoryListState.loading || bigTagListState.loading,
			masterErrors: flatten([categoryListState.error, bigTagListState.error])
		}),
		[categoryListState, bigTagListState]
	)
	const mrpCondition = condition?.source === 'mrp' ? condition as PredictionConditionMrp : null
	const form = useForm({
		defaultValues: mrpCondition ? {
			productId: mrpCondition.productId ?? [],
			startWeekday: mrpCondition.startWeekday ?? undefined,
			startHour: mrpCondition.startHour ?? undefined,
			days: mrpCondition.days ?? undefined,
			serviceType: mrpCondition.serviceType,
			contentsType: mrpCondition.contentsType,
			contentsDuration: mrpCondition.contentsDuration,
			completeUp: mrpCondition.completeUp,
			recommendPriolity: mrpCondition.recommendPriolity,
			areaMrp: mrpCondition.areaMrp,
			categoryCode: mrpCondition.categoryCode ?? [],
			bigTagId: mrpCondition.bigTagId ?? []
		} : {
			productId: [],
			startWeekday: Object.keys(WeekDays)[(new Date()).getDay()],
			startHour: 19,
			days: 100,
			serviceType: undefined,
			contentsType: undefined,
			contentsDuration: 60,
			completeUp: 600,
			recommendPriolity: undefined,
			areaMrp: undefined,
			categoryCode: [],
			bigTagId: []
		},
		mode: 'onChange',
		resolver: yupResolver(formSchema)
	})
	const submit = useCallback(
		(input: FormType) => {
			const isMovie = input.contentsType == 'movie' || input.contentsType == 'mixed'
			const condition: PredictionConditionMrp = {
				source: 'mrp',
				productId: (input.productId && input.productId.length > 0) ? input.productId : undefined,
				startWeekday: input.startWeekday ? (input.startWeekday as WeekDay) : undefined,
				startHour: input.startHour,
				days: input.days,
				serviceType: input.serviceType ? (input.serviceType as MrpPredictionServiceType) : undefined,
				contentsType: input.contentsType ? (input.contentsType as MrpPredictionContentsType) : undefined,
				contentsDuration: isMovie ? input.contentsDuration ?? undefined : undefined,
				completeUp: input.completeUp,
				recommendPriolity: input.recommendPriolity ? (input.recommendPriolity as MrpPredictionRecommendPriolity) : undefined,
				areaMrp: input.areaMrp ? (input.areaMrp as MrpPredictionAreaMrpType) : undefined,
				categoryCode: (input.categoryCode && input.categoryCode.length > 0) ? input.categoryCode : undefined,
				bigTagId: (input.bigTagId && input.bigTagId.length > 0) ? input.bigTagId : undefined
			}
			condition.hash = JSON.stringify(condition)
			condition.text = flatJoin(', ',
				condition.productId?.flatMap(productId =>
					SpecialProducts.find(product => product.productId === productId)?.productName ??
					productList.find(product => product.productId === productId)?.productName ?? []
				).join(', '),
				condition.bigTagId?.flatMap(bigTagId => bigTagListState.data?.list.find(bigTag => bigTag.tagId === bigTagId)?.tagName ?? []).join(', '),
				condition.categoryCode?.flatMap(categoryCode => categoryListState.data?.list.find(category => category.categoryCode === categoryCode)?.categoryName ?? []).join(', '),
				condition.startWeekday ? WeekDays[condition.startWeekday].long : undefined,
				condition.startHour ? `${condition.startHour}時` : undefined,
				condition.days ? `${condition.days}日間` : undefined,
				condition.serviceType ? MrpPredictionServiceTypes[condition.serviceType] : undefined,
				condition.contentsType ? MrpPredictionContentsTypes[condition.contentsType] : undefined,
				condition.contentsDuration ? `${condition.contentsDuration}秒間` : undefined,
				condition.completeUp ? `視聴時${condition.completeUp}UP` : undefined,
				condition.recommendPriolity ? MrpPredictionRecommendPriolities[condition.recommendPriolity] : undefined,
				condition.areaMrp ? MrpPredictionAreaMrpTypes[condition.areaMrp] : undefined
			)
			return condition
		},
		[productList, bigTagListState.data, categoryListState.data]
	)
	return {
		form, submit,
		productList,
		masterLoading, masterErrors,
		categoryList: categoryListState.data?.list ?? [],
		bigTagList: bigTagListState.data?.list ?? []
	}
}

export const MrpPredictionForm = ({
	form: {
    control,
    formState: { errors },
		watch,
  },
	masterLoading, masterErrors,
	productList, categoryList, bigTagList,
	...others
}: ReturnType<typeof useMrpPredictionForm> & StackProps) => {
	const contentsType = watch("contentsType")
	return (
		<Stack direction="column" {...others}>
			{masterLoading && <Loading/>}
			{masterErrors.length > 0 && <ErrorBlock error={masterErrors}/>}

			<Box>
				<Section label="対象製品" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="productId"
						render={({field}) => (
							<>
								<OptionSelect
									{...field}
									variant="outlined"
									size="small"
									optionList={productList.map(product => ({
										id: product.productId,
										label: product.productName
									})).concat(SpecialProducts.map(product => ({
										id: product.productId,
										label: product.productName
									})))}
								/>
								{errors.productId && (
									<FormHelperText error>{errors.productId.message}</FormHelperText>
								)}
							</>
						)}
					/> 
				</Section>

				<Section label="診療科" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="bigTagId"
						render={({field}) => (
							<>
								<OptionSelect
									{...field}
									variant="outlined"
									size="small"
									optionList={bigTagList.map((tag) => ({
										id: tag.tagId,
										label: tag.tagName ?? ''
									}))}
								/>
								{errors.bigTagId && (
									<FormHelperText error>{errors.bigTagId.message}</FormHelperText>
								)}
							</>
						)}
					/>
				</Section>

				<Section label="薬効分類" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="categoryCode"
						render={({field}) => (
							<>
								<OptionSelect
									{...field}
									variant="outlined"
									size="small"
									optionList={categoryList.map((category) => ({
										id: category.categoryCode,
										label: category.categoryName ?? ''
									}))}
								/>
								{errors.categoryCode && (
									<FormHelperText error>{errors.categoryCode.message}</FormHelperText>
								)}
							</>
						)}
					/>
				</Section>

				<Section label="配信予定" labelProps={{ py: 2 }} innerProps={{ spacing: 2 }}>
					<Controller
						control={control}
						name="startWeekday"
						render={({ field }) => (
							<ToggleButtonGroup
								aria-label="startWeekday"
								exclusive
								color="primary"
								size="small"
								{...field}
							>
								{Object.entries(WeekDays).map(([key, label]) => (
									<ToggleButton key={`startWeekday-${key}`} value={key} >{label.long}</ToggleButton>
								))}
							</ToggleButtonGroup>
						)}
					/>
					{errors.startWeekday && (
						<FormHelperText error>{errors.startWeekday.message}</FormHelperText>
					)}
					<Stack direction="row" spacing={1} alignItems="center">
						<Controller
							control={control}
							name="startHour"
							render={({ field }) => (
								<FormControl sx={{ maxWidth: '6em' }} variant="outlined">
									<OutlinedInput
										{...field}
										endAdornment={<InputAdornment position="end">時</InputAdornment>}
										size="small"
										error={!!errors.startHour}
									/>
								</FormControl>
							)}
						/>
						<Typography variant='body2' whiteSpace='nowrap'>から</Typography>
						<Controller
							control={control}
							name="days"
							render={({ field }) => (
								<FormControl sx={{ maxWidth: '7em' }} variant="outlined">
									<OutlinedInput
										{...field}
										endAdornment={<InputAdornment position="end">日間</InputAdornment>}
										size="small"
										error={!!errors.days}
									/>
								</FormControl>
							)}
						/>
					</Stack>
					{errors.startHour && (
						<FormHelperText error>{errors.startHour.message}</FormHelperText>
					)}
					{errors.days && (
						<FormHelperText error>{errors.days.message}</FormHelperText>
					)}
				</Section>

				<Section label="サービスタイプ" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="serviceType"
						render={({ field }) => (
							<FormControl>
								<RadioGroup
									{...field}
									row
								>
									{Object.entries(MrpPredictionServiceTypes).map(([key, label]) => (
										<FormControlLabel key={`serviceType-${key}`} value={key} control={<Radio/>} label={label} />
									))}
								</RadioGroup>
								{errors.serviceType && (
									<FormHelperText error>{errors.serviceType.message}</FormHelperText>
								)}
							</FormControl>
						)}
					/>
				</Section>

				<Section label="配信コンテンツ" labelProps={{ py: 2 }} innerProps={{ spacing: 0.5 }}>
					<Controller
						control={control}
						name="contentsType"
						render={({ field }) => (
							<FormControl>
								<RadioGroup
									{...field}
									row
								>
									{Object.entries(MrpPredictionContentsTypes).map(([key, label]) => (
										<FormControlLabel key={`contentsType-${key}`} value={key} control={<Radio/>} label={label} />
									))}
								</RadioGroup>
								{errors.contentsType && (
									<FormHelperText error>{errors.contentsType.message}</FormHelperText>
								)}
							</FormControl>
						)}
					/>
					{(contentsType === 'movie' || contentsType === 'mixed') && (
						<Controller
							control={control}
							name="contentsDuration"
							render={({ field }) => (
								<FormControl sx={{ maxWidth: '7em' }} variant="outlined">
									<OutlinedInput
										{...field}
										endAdornment={<InputAdornment position="end">秒間</InputAdornment>}
										size="small"
										error={!!errors.contentsDuration}
									/>
									{errors.contentsDuration && (
										<FormHelperText error>{errors.contentsDuration.message}</FormHelperText>
									)}
								</FormControl>
							)}
						/>
					)}
				</Section>

				<Section label="付与UP" labelProps={{ py: 2 }} disablePaddingY>
					<Stack direction="row" spacing={2} alignItems="center">
						<Stack direction="row" spacing={1} alignItems="center">
							<Typography py={2} variant='body2' whiteSpace='nowrap'>視聴時</Typography>
							<Controller
								control={control}
								name="completeUp"
								render={({ field }) => (
									<FormControl sx={{ maxWidth: '6em' }} variant="outlined">
										<OutlinedInput
											{...field}
											endAdornment={<InputAdornment position="end">UP</InputAdornment>}
											size="small"
											error={!!errors.completeUp}
										/>
									</FormControl>
								)}
							/>
						</Stack>
					</Stack>
					{errors.completeUp && (
						<FormHelperText error>{errors.completeUp.message}</FormHelperText>
					)}
				</Section>

				<Section label="おすすめ優先度" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="recommendPriolity"
						render={({ field }) => (
							<FormControl>
								<RadioGroup
									{...field}
									row
								>
									{Object.entries(MrpPredictionRecommendPriolities).map(([key, label]) => (
										<FormControlLabel key={`recommendPriolity-${key}`} value={key} control={<Radio/>} label={label} />
									))}
								</RadioGroup>
								{errors.recommendPriolity && (
									<FormHelperText error>{errors.recommendPriolity.message}</FormHelperText>
								)}
							</FormControl>
						)}
					/>
				</Section>

				<Section label="地域限定配信" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="areaMrp"
						render={({ field }) => (
							<FormControl>
								<RadioGroup
									{...field}
									row
								>
									{Object.entries(MrpPredictionAreaMrpTypes).map(([key, label]) => (
										<FormControlLabel key={`areaMrp-${key}`} value={key} control={<Radio/>} label={label} />
									))}
								</RadioGroup>
								{errors.areaMrp && (
									<FormHelperText error>{errors.areaMrp.message}</FormHelperText>
								)}
							</FormControl>
						)}
					/>
				</Section>
			</Box>
		</Stack>
	)
}